import { createContext, useContext } from 'react'

import sanitizer from '../../../helpers/sanitizer'
import { useAuth } from '../../Shared/Contexts/AuthContext'

const OrganizationsContext = createContext({})

export const useOrganizations = () => {
  return useContext(OrganizationsContext)
}

export const OrganizationsProvider = ({ children }) => {
  const {
    useBaseAxiosGet,
    useBaseAxiosPost,
    useBaseAxiosPatch,
    useBaseAxiosDelete,
    useBaseAxiosPut
  } = useAuth()

  const useFetchOrganization = async (organizationKey) => {
    return await useBaseAxiosGet({ url: `/organizations/${organizationKey}` })
  }

  const useFetchKeySuggestion = async (name) => {
    return await useBaseAxiosGet({
      url: `/organization-key-suggestion?name=${name}`
    })
  }

  const useCreateRootOrganization = async (payload) => {
    const formPayload = new FormData()
    formPayload.append('data', JSON.stringify(payload.data))
    if (payload.logo) {
      formPayload.append('logo', payload.logo)
    }
    return await useBaseAxiosPost({
      url: '/organizations',
      payload: formPayload
    })
  }

  const useCreateChildOrganization = async ({
    parentOrganization,
    payload
  }) => {
    const formPayload = new FormData()
    formPayload.append('data', JSON.stringify(payload.data))
    if (payload.logo) {
      formPayload.append('logo', payload.logo)
    }
    return await useBaseAxiosPost({
      url: `/organizations/${parentOrganization}/children`,
      payload: formPayload
    })
  }

  const useEditOrganization = async (payload) => {
    const formPayload = new FormData()
    formPayload.append('data', JSON.stringify(payload.data))
    if (payload.logo) {
      formPayload.append('logo', payload.logo)
    }
    return await useBaseAxiosPut({
      url: `/organizations/${payload.organizationKey}`,
      payload: formPayload
    })
  }

  const useFetchTimezones = async () => {
    return await useBaseAxiosGet({ url: '/timezones' })
  }

  const useFetchAllNetworkServers = async () => {
    return await useBaseAxiosGet({
      url: '/network-servers?filter=onlyAvailableUsers'
    })
  }

  const useFetchOrganizationProfiles = async () => {
    return await useBaseAxiosGet({
      url: '/organization-profiles'
    })
  }

  const useFetchOrganizationProfile = async (profileId) => {
    return await useBaseAxiosGet({
      url: `/organization-profiles/${profileId}`
    })
  }

  const useFetchOrganizationsWithThresholdsAndFilters = async ({
    filters,
    fullText,
    page,
    pageSize
  }) => {
    return await useBaseAxiosPost({
      url: '/organizations/search',
      params: fullText ? {
        fullText,
        page,
        pageSize
      } : {
        page,
        pageSize
      },
      payload:
        filters.organizationKeys?.length > 0
          ? sanitizer({
            ...filters,
            searchInSubOrganizations: true
          })
          : {}
    })
  }

  const useLockOrganization = async (organizationKey) => {
    return await useBaseAxiosPatch({
      url: `/organizations/${organizationKey}/lock`
    })
  }

  const useUnlockOrganization = async (organizationKey) => {
    return await useBaseAxiosPatch({
      url: `/organizations/${organizationKey}/unlock`
    })
  }
  const useDeleteOrganization = async (organizationKey) => await useBaseAxiosDelete({
    url: `/organizations/${organizationKey}`
  })

  const value = {
    useCreateRootOrganization,
    useCreateChildOrganization,
    useEditOrganization,
    useFetchOrganization,
    useFetchKeySuggestion,
    useFetchTimezones,
    useFetchAllNetworkServers,
    useFetchOrganizationProfiles,
    useFetchOrganizationProfile,
    useFetchOrganizationsWithThresholdsAndFilters,
    useLockOrganization,
    useUnlockOrganization,
    useDeleteOrganization
  }

  return (
    <OrganizationsContext.Provider value={value}>
      {children}
    </OrganizationsContext.Provider>
  )
}
